import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Skeleton,
  Snackbar,
  Alert,
  IconButton,
  MenuItem,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import {
  Target,
  Save,
  Plus,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Search,
  Trophy,
} from "lucide-react";
import { apiFetch } from "../utils/api";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface Game {
  game_uid: string;
  game_name: string;
  vendor: string | null;
  game_type: string | null;
  game_icon: string | null;
}

interface Challenge {
  id: number;
  title: string | null;
  game_uid: string;
  game_name: string | null;
  vendor: string | null;
  game_icon: string | null;
  target_multiplier: number;
  min_bet_usd: number;
  reward_amount: number;
  reward_currency: string;
  status: "active" | "finished" | "disabled";
  winner_user_id: number | null;
  winner_name: string | null;
  won_multiplier: number | null;
  won_at: string | null;
  sort_order: number;
}

interface Reward {
  id: number;
  challenge_id: number;
  user_id: number;
  user_name: string | null;
  currency: string;
  amount: number;
  multiplier: number | null;
  bet_usd: number | null;
  game_name: string | null;
  title: string | null;
  claim: boolean;
  created_at: string;
}

/* ------------------------------------------------------------------ */
/*  Shared styles (match spinWheel)                                    */
/* ------------------------------------------------------------------ */
const cardSx = { bgcolor: "#0E1831", border: "1px solid #1E2D55", borderRadius: 2 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#0B1427",
    "& fieldset": { borderColor: "#1E2D55" },
    "&:hover fieldset": { borderColor: "#2B6EF5" },
    "&.Mui-focused fieldset": { borderColor: "#2B6EF5" },
  },
  "& input": { color: "#F9F9F9" },
  "& .MuiInputLabel-root": { color: "#8384A5" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#2B6EF5" },
};

const thSx = {
  bgcolor: "#0B1427",
  color: "#8384A5",
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  borderBottom: "1px solid #1E2D55",
  whiteSpace: "nowrap" as const,
};

const tdSx = {
  color: "#C8CAE5",
  borderBottom: "1px solid #1E2D55",
  fontSize: "0.82rem",
  py: 1.5,
};

const statusColor: Record<string, { bg: string; fg: string }> = {
  active: { bg: "rgba(14,204,104,0.12)", fg: "#0ECC68" },
  finished: { bg: "rgba(43,110,245,0.12)", fg: "#5581F7" },
  disabled: { bg: "rgba(131,132,165,0.12)", fg: "#8384A5" },
};

const PAGE_SIZE = 15;

type FormState = {
  id?: number;
  game_uid: string;
  game_name: string;
  game_icon: string | null;
  vendor: string | null;
  title: string;
  target_multiplier: string;
  min_bet_usd: string;
  reward_amount: string;
};

const emptyForm: FormState = {
  game_uid: "",
  game_name: "",
  game_icon: null,
  vendor: null,
  title: "",
  target_multiplier: "",
  min_bet_usd: "1",
  reward_amount: "",
};

/* ------------------------------------------------------------------ */
/*  Game picker dialog                                                 */
/* ------------------------------------------------------------------ */
const GamePicker: React.FC<{
  open: boolean;
  onClose: () => void;
  onPick: (g: Game) => void;
}> = ({ open, onClose, onPick }) => {
  const [keyword, setKeyword] = useState("");
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (kw: string) => {
    setLoading(true);
    try {
      const res = await apiFetch<{ success: boolean; data: Game[] }>(
        `/api/challenges/admin/games?keyword=${encodeURIComponent(kw)}&limit=30`
      );
      setGames(res.data || []);
    } catch (e) {
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(keyword), 300);
    return () => clearTimeout(t);
  }, [keyword, open, search]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: "#0E1831", border: "1px solid #1E2D55", borderRadius: 2 } }}>
      <DialogTitle sx={{ color: "#F9F9F9", fontWeight: 700 }}>Select a game</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search by game or vendor…"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          sx={{ ...inputSx, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color="#8384A5" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {loading ? (
            <Stack spacing={1}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ bgcolor: "#162140", borderRadius: 1 }} />
              ))}
            </Stack>
          ) : games.length === 0 ? (
            <Typography sx={{ color: "#8384A5", textAlign: "center", py: 4 }}>No games found.</Typography>
          ) : (
            <Stack spacing={0.5}>
              {games.map((g) => (
                <Box
                  key={g.game_uid}
                  onClick={() => onPick(g)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#162140" },
                  }}
                >
                  <Avatar src={g.game_icon || undefined} variant="rounded" sx={{ width: 36, height: 36, bgcolor: "#162140" }}>
                    {g.game_name?.[0] || "?"}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#F9F9F9", fontSize: "0.85rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {g.game_name}
                    </Typography>
                    <Typography sx={{ color: "#8384A5", fontSize: "0.72rem" }}>{g.vendor || "—"}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#8384A5", textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const Challenges: React.FC = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "finished" | "disabled">("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [rewards, setRewards] = useState<Reward[]>([]);
  const [rewardsTotal, setRewardsTotal] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" }>({
    open: false,
    msg: "",
    sev: "success",
  });

  const notify = (msg: string, sev: "success" | "error" = "success") => setSnack({ open: true, msg, sev });

  /* ---- fetchers ---- */
  const fetchChallenges = useCallback(async (p = 1, status = statusFilter) => {
    try {
      const sQ = status === "all" ? "" : `&status=${status}`;
      const res = await apiFetch<{ success: boolean; data: Challenge[]; total: number }>(
        `/api/challenges/admin/list?page=${p}&limit=${PAGE_SIZE}${sQ}`
      );
      setChallenges(res.data || []);
      setTotal(res.total || 0);
      setPage(p);
    } catch (e: any) {
      notify(e.message || "Failed to load challenges", "error");
    }
  }, [statusFilter]);

  const fetchRewards = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: Reward[]; total: number; total_paid: number }>(
        `/api/challenges/admin/rewards?page=1&limit=15`
      );
      setRewards(res.data || []);
      setRewardsTotal(res.total || 0);
      setTotalPaid(res.total_paid || 0);
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchChallenges(1, statusFilter), fetchRewards()]).then(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  /* ---- form helpers ---- */
  const setField = (k: keyof FormState, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () => setForm(emptyForm);

  const pickGame = (g: Game) => {
    setForm((f) => ({
      ...f,
      game_uid: g.game_uid,
      game_name: g.game_name,
      game_icon: g.game_icon,
      vendor: g.vendor,
    }));
    setPickerOpen(false);
  };

  const editChallenge = (c: Challenge) => {
    setForm({
      id: c.id,
      game_uid: c.game_uid,
      game_name: c.game_name || "",
      game_icon: c.game_icon,
      vendor: c.vendor,
      title: c.title || "",
      target_multiplier: String(c.target_multiplier),
      min_bet_usd: String(c.min_bet_usd),
      reward_amount: String(c.reward_amount),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---- save (create or update) ---- */
  const handleSave = async () => {
    if (!form.game_uid) return notify("Pick a game first", "error");
    const target = parseFloat(form.target_multiplier);
    const minBet = parseFloat(form.min_bet_usd);
    const reward = parseFloat(form.reward_amount);
    if (!(target > 0)) return notify("Target multiplier must be > 0", "error");
    if (!(minBet > 0)) return notify("Min bet must be > 0", "error");
    if (!(reward > 0)) return notify("Reward must be > 0", "error");

    setSaving(true);
    try {
      if (form.id) {
        await apiFetch("/api/challenges/admin/update", {
          method: "POST",
          body: JSON.stringify({
            id: form.id,
            title: form.title || null,
            target_multiplier: target,
            min_bet_usd: minBet,
            reward_amount: reward,
          }),
        });
        notify("Challenge updated!");
      } else {
        await apiFetch("/api/challenges/admin/create", {
          method: "POST",
          body: JSON.stringify({
            game_uid: form.game_uid,
            title: form.title || null,
            target_multiplier: target,
            min_bet_usd: minBet,
            reward_amount: reward,
          }),
        });
        notify("Challenge created!");
      }
      resetForm();
      await fetchChallenges(form.id ? page : 1, statusFilter);
    } catch (e: any) {
      notify(e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ---- status change / delete ---- */
  const setStatus = async (c: Challenge, status: "active" | "finished" | "disabled") => {
    try {
      await apiFetch("/api/challenges/admin/update", {
        method: "POST",
        body: JSON.stringify({ id: c.id, status }),
      });
      notify(`Marked ${status}`);
      await fetchChallenges(page, statusFilter);
    } catch (e: any) {
      notify(e.message || "Failed", "error");
    }
  };

  const handleDelete = async (c: Challenge) => {
    if (!window.confirm(`Delete challenge for "${c.game_name || c.game_uid}"?`)) return;
    try {
      await apiFetch("/api/challenges/admin/delete", {
        method: "POST",
        body: JSON.stringify({ id: c.id }),
      });
      notify("Deleted");
      await fetchChallenges(page, statusFilter);
    } catch (e: any) {
      notify(e.message || "Failed to delete", "error");
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
        <Skeleton variant="rectangular" height={80} sx={{ bgcolor: "#162140", borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={220} sx={{ bgcolor: "#162140", borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ bgcolor: "#162140", borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{ p: 1, bgcolor: "rgba(255,194,63,0.15)", borderRadius: 2, display: "flex" }}>
          <Target size={22} color="#FFC23F" />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ color: "#F9F9F9", fontWeight: 700 }}>
            Challenges Management
          </Typography>
          <Typography variant="caption" sx={{ color: "#8384A5" }}>
            Create "first to hit Nx" game challenges — set reward, min bet & target per game
          </Typography>
        </Box>
      </Box>

      {/* ═══ FORM CARD ═══ */}
      <Paper sx={{ ...cardSx, mb: 3, overflow: "hidden" }}>
        <Box sx={{ px: 3, py: 2, bgcolor: "#0B1427", borderBottom: "1px solid #1E2D55", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ color: "#F9F9F9", fontWeight: 700 }}>
            {form.id ? `Edit Challenge #${form.id}` : "New Challenge"}
          </Typography>
          {form.id && (
            <Button size="small" onClick={resetForm} sx={{ color: "#8384A5", textTransform: "none" }}>
              Cancel edit
            </Button>
          )}
        </Box>
        <Box sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            {/* Game selector */}
            <Box sx={{ flex: 1.4 }}>
              <Typography sx={{ color: "#8384A5", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", mb: 0.75 }}>
                Game
              </Typography>
              <Box
                onClick={() => !form.id && setPickerOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1,
                  minHeight: 56,
                  bgcolor: "#0B1427",
                  border: "1px solid #1E2D55",
                  borderRadius: 1,
                  cursor: form.id ? "default" : "pointer",
                  "&:hover": form.id ? {} : { borderColor: "#2B6EF5" },
                }}
              >
                {form.game_uid ? (
                  <>
                    <Avatar src={form.game_icon || undefined} variant="rounded" sx={{ width: 40, height: 40, bgcolor: "#162140" }}>
                      {form.game_name?.[0] || "?"}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ color: "#F9F9F9", fontSize: "0.85rem", fontWeight: 600 }}>{form.game_name}</Typography>
                      <Typography sx={{ color: "#8384A5", fontSize: "0.72rem" }}>{form.vendor || "—"}</Typography>
                    </Box>
                    {!form.id && <Pencil size={15} color="#8384A5" />}
                  </>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "#8384A5", px: 1 }}>
                    <Plus size={16} />
                    <Typography sx={{ fontSize: "0.85rem" }}>Select a game…</Typography>
                  </Stack>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: "#8384A5", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", mb: 0.75 }}>
                Title (optional)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Defaults to game name"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                sx={inputSx}
              />
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
            <TextField
              label="Target multiplier (x)"
              type="number"
              size="small"
              fullWidth
              value={form.target_multiplier}
              onChange={(e) => setField("target_multiplier", e.target.value)}
              sx={inputSx}
              InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ color: "#8384A5" }}>x</Typography></InputAdornment> }}
            />
            <TextField
              label="Minimum bet (USD)"
              type="number"
              size="small"
              fullWidth
              value={form.min_bet_usd}
              onChange={(e) => setField("min_bet_usd", e.target.value)}
              sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: "#8384A5" }}>$</Typography></InputAdornment> }}
            />
            <TextField
              label="Reward (USDT)"
              type="number"
              size="small"
              fullWidth
              value={form.reward_amount}
              onChange={(e) => setField("reward_amount", e.target.value)}
              sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: "#8384A5" }}>$</Typography></InputAdornment> }}
            />
          </Stack>

          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            startIcon={form.id ? <Save size={15} /> : <Plus size={15} />}
            sx={{ bgcolor: "#2B6EF5", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "#4A85F7" } }}
          >
            {saving ? "Saving..." : form.id ? "Save Changes" : "Create Challenge"}
          </Button>
        </Box>
      </Paper>

      {/* ═══ CHALLENGES TABLE ═══ */}
      <Paper sx={{ ...cardSx, mb: 3, overflow: "hidden" }}>
        <Box sx={{ px: 3, py: 2, bgcolor: "#0B1427", borderBottom: "1px solid #1E2D55", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ color: "#F9F9F9", fontWeight: 700 }}>All Challenges</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              sx={{ ...inputSx, minWidth: 130, "& .MuiSelect-select": { color: "#F9F9F9", py: 0.75 } }}
            >
              {["all", "active", "finished", "disabled"].map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <Chip label={`${total} total`} size="small" sx={{ bgcolor: "rgba(43,110,245,0.12)", color: "#5581F7", fontWeight: 600 }} />
          </Stack>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Game</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Target</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Min Bet</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Reward</TableCell>
                <TableCell sx={thSx}>Status</TableCell>
                <TableCell sx={thSx}>Winner</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {challenges.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, borderBottom: "none" }}>
                    <Target size={36} color="#1E2D55" />
                    <Typography sx={{ color: "#8384A5", mt: 1 }}>No challenges yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                challenges.map((c) => (
                  <TableRow key={c.id} sx={{ "&:hover": { bgcolor: "rgba(43,110,245,0.04)" }, transition: "background 150ms" }}>
                    <TableCell sx={tdSx}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar src={c.game_icon || undefined} variant="rounded" sx={{ width: 34, height: 34, bgcolor: "#162140" }}>
                          {(c.game_name || "?")[0]}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: "#F9F9F9", fontSize: "0.82rem", fontWeight: 600 }}>
                            {c.title || c.game_name || c.game_uid}
                          </Typography>
                          <Typography sx={{ color: "#8384A5", fontSize: "0.7rem" }}>{c.vendor || "—"}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontFamily: "monospace", color: "#FFC23F", fontWeight: 600 }}>
                      {Number(c.target_multiplier).toLocaleString()}x
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontFamily: "monospace" }}>
                      ${Number(c.min_bet_usd).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                      <Chip
                        label={`$${Number(c.reward_amount).toLocaleString()}`}
                        size="small"
                        sx={{ bgcolor: "rgba(14,204,104,0.12)", color: "#0ECC68", fontWeight: 600, fontFamily: "monospace" }}
                      />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Chip
                        label={c.status}
                        size="small"
                        sx={{ bgcolor: statusColor[c.status]?.bg, color: statusColor[c.status]?.fg, fontWeight: 600, textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      {c.winner_user_id ? (
                        <Box>
                          <Typography sx={{ color: "#5581F7", fontSize: "0.78rem", fontWeight: 600 }}>
                            {c.winner_name || `#${c.winner_user_id}`}
                          </Typography>
                          {c.won_multiplier != null && (
                            <Typography sx={{ color: "#8384A5", fontSize: "0.68rem" }}>
                              @ {Number(c.won_multiplier).toLocaleString()}x
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ color: "#8384A5" }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <IconButton size="small" onClick={() => editChallenge(c)} sx={{ color: "#8384A5", "&:hover": { color: "#2B6EF5" } }} title="Edit">
                          <Pencil size={15} />
                        </IconButton>
                        {c.status === "active" ? (
                          <IconButton size="small" onClick={() => setStatus(c, "disabled")} sx={{ color: "#8384A5", "&:hover": { color: "#FFC23F" } }} title="Disable">
                            <Target size={15} />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => setStatus(c, "active")} sx={{ color: "#8384A5", "&:hover": { color: "#0ECC68" } }} title="Re-activate (clears winner)">
                            <Trophy size={15} />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => handleDelete(c)} sx={{ color: "#8384A5", "&:hover": { color: "#E01B4F" } }} title="Delete">
                          <Trash2 size={15} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "center", borderTop: "1px solid #1E2D55", gap: 1.5 }}>
            <Button
              size="small"
              disabled={page <= 1}
              onClick={() => fetchChallenges(page - 1, statusFilter)}
              startIcon={<ChevronLeft size={14} />}
              sx={{ border: "1px solid #1E2D55", color: "#8384A5", textTransform: "none", fontSize: "0.75rem", "&:hover": { borderColor: "#2B6EF5", color: "#2B6EF5" }, "&:disabled": { opacity: 0.4 } }}
            >
              Prev
            </Button>
            <Typography variant="caption" sx={{ color: "#8384A5" }}>
              Page {page} of {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page >= totalPages}
              onClick={() => fetchChallenges(page + 1, statusFilter)}
              endIcon={<ChevronRight size={14} />}
              sx={{ border: "1px solid #1E2D55", color: "#8384A5", textTransform: "none", fontSize: "0.75rem", "&:hover": { borderColor: "#2B6EF5", color: "#2B6EF5" }, "&:disabled": { opacity: 0.4 } }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* ═══ WINNERS / REWARD HISTORY ═══ */}
      <Paper sx={{ ...cardSx, overflow: "hidden" }}>
        <Box sx={{ px: 3, py: 2, bgcolor: "#0B1427", borderBottom: "1px solid #1E2D55", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ color: "#F9F9F9", fontWeight: 700 }}>Winners & Payouts</Typography>
          <Stack direction="row" spacing={1}>
            <Chip label={`$${Number(totalPaid).toLocaleString()} paid`} size="small" sx={{ bgcolor: "rgba(14,204,104,0.12)", color: "#0ECC68", fontWeight: 600 }} />
            <Chip label={`${rewardsTotal} total`} size="small" sx={{ bgcolor: "rgba(43,110,245,0.12)", color: "#5581F7", fontWeight: 600 }} />
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>User</TableCell>
                <TableCell sx={thSx}>Game</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Multiplier</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Bet (USD)</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "right" }}>Reward</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>Claim</TableCell>
                <TableCell sx={thSx}>Won At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rewards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 5, borderBottom: "none" }}>
                    <Trophy size={32} color="#1E2D55" />
                    <Typography sx={{ color: "#8384A5", mt: 1 }}>No winners yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rewards.map((r) => (
                  <TableRow key={r.id} sx={{ "&:hover": { bgcolor: "rgba(43,110,245,0.04)" } }}>
                    <TableCell sx={{ ...tdSx, color: "#5581F7", fontWeight: 600 }}>
                      {r.user_name || `#${r.user_id}`}
                    </TableCell>
                    <TableCell sx={tdSx}>{r.title || r.game_name || "—"}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontFamily: "monospace", color: "#FFC23F" }}>
                      {r.multiplier != null ? `${Number(r.multiplier).toLocaleString()}x` : "—"}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontFamily: "monospace" }}>
                      {r.bet_usd != null ? `$${Number(r.bet_usd).toLocaleString()}` : "—"}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                      <Chip
                        label={`+$${Number(r.amount).toLocaleString()}`}
                        size="small"
                        sx={{ bgcolor: "rgba(14,204,104,0.12)", color: "#0ECC68", fontWeight: 600, fontFamily: "monospace" }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                      <Chip
                        label={r.claim ? "Claimed" : "Pending"}
                        size="small"
                        sx={
                          r.claim
                            ? { bgcolor: "rgba(14,204,104,0.12)", color: "#0ECC68", fontWeight: 600 }
                            : { bgcolor: "rgba(255,194,63,0.12)", color: "#FFC23F", fontWeight: 600 }
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, color: "#8384A5", fontSize: "0.75rem" }}>
                      {new Date(r.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <GamePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={pickGame} />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.sev}
          sx={{
            bgcolor: snack.sev === "success" ? "rgba(14,204,104,0.15)" : "rgba(224,27,79,0.15)",
            color: snack.sev === "success" ? "#0ECC68" : "#E01B4F",
            border: `1px solid ${snack.sev === "success" ? "#0ECC68" : "#E01B4F"}44`,
            "& .MuiAlert-icon": { color: snack.sev === "success" ? "#0ECC68" : "#E01B4F" },
          }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Challenges;
